import React, { useState } from 'react';
import { StrikeStage } from './types';
import { useAudio } from '../../audio/useAudio';

interface SeveringOfYokesProps {
  onStrikeSuccess?: () => void;
}

export const SeveringOfYokes: React.FC<SeveringOfYokesProps> = ({ onStrikeSuccess }) => {
  const [yokeInput, setYokeInput] = useState<string>('');
  const [stage, setStage] = useState<StrikeStage>('idle');
  const [inscribedText, setInscribedText] = useState<string>('');
  const [isScreenShaking, setIsScreenShaking] = useState<boolean>(false);
  const { playAnvilStrike } = useAudio();

  const handleInscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!yokeInput.trim()) return;
    setInscribedText(yokeInput.trim());
    setStage('inscribed');
  };

  const handleConsecrate = () => {
    setStage('consecrating');
    setTimeout(() => {
      setStage('striking');
      triggerHammerStrike();
    }, 1200);
  };

  const triggerHammerStrike = () => {
    // 1. Audio Anvil Strike
    playAnvilStrike();

    // 2. Visual Screen Shake
    setIsScreenShaking(true);
    setTimeout(() => setIsScreenShaking(false), 700);

    // 3. Fracture & Dissolve
    setTimeout(() => {
      setStage('dissolving');
    }, 400);

    // 4. Memory Scrubbing & Cleansing
    setTimeout(() => {
      // Zero-retention memory scrubbing
      setYokeInput('');
      setInscribedText('');
      setStage('cleansed');
      if (onStrikeSuccess) onStrikeSuccess();
    }, 1800);
  };

  const handleReset = () => {
    setYokeInput('');
    setInscribedText('');
    setStage('idle');
  };

  return (
    <div
      className={`chthonic-card rounded-lg p-6 sm:p-8 lg:p-10 space-y-8 transition-transform duration-100 ${
        isScreenShaking ? 'translate-x-1 -translate-y-1 rotate-[0.5deg]' : ''
      }`}
      id="severing-of-yokes-component"
    >
      {/* Header */}
      <div className="border-b border-iron-dark pb-4">
        <div className="flex items-center gap-2 text-xs font-mono text-ember-spark uppercase tracking-widest">
          <span>RITUS INCUDIS</span>
          <span>•</span>
          <span>SEVERING OF YOKES (BELI-'OL)</span>
        </div>
        <h3 className="font-display text-2xl sm:text-3xl text-parchment-light uppercase mt-1">
          The Anvil of Severance
        </h3>
        <p className="text-xs sm:text-sm text-parchment-dim mt-2 max-w-2xl font-serif">
          Inscribe personal bondages, ancestral liens, unconsented covenants, or dogmatic guilt upon the iron face. 
          Strike the anvil to fracture their hold and permanently expunge them from reality.
        </p>
      </div>

      {/* STAGE 1: INSCRIBING (Idle) */}
      {stage === 'idle' && (
        <form onSubmit={handleInscribe} className="space-y-4 max-w-2xl">
          <label htmlFor="yoke-bondage-input" className="block text-xs font-mono text-gold-bright uppercase tracking-wider">
            Inscribe Bondage, False Vow, Manipulative Debt, or Internalized Guilt
          </label>
          <textarea
            id="yoke-bondage-input"
            rows={4}
            value={yokeInput}
            onChange={(e) => setYokeInput(e.target.value)}
            placeholder="e.g. Inherited religious shame, unconsented family expectations, toxic covenants, financial anxiety..."
            className="w-full rounded border border-iron-base bg-obsidian-deep p-4 text-xs sm:text-sm font-mono text-parchment-light placeholder-iron-muted focus:border-gold-bright focus:outline-none focus:ring-1 focus:ring-gold-bright"
            required
          />
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-serif text-iron-steel italic">
              "What was entered without informed consent is null ab initio."
            </span>
            <button
              type="submit"
              disabled={!yokeInput.trim()}
              className="btn-gold px-6 py-2.5 text-xs font-semibold rounded disabled:opacity-50"
            >
              Cast onto Cold Anvil →
            </button>
          </div>
        </form>
      )}

      {/* STAGE 2: INSCRIBED / CONSECRATING / STRIKING / DISSOLVING */}
      {(stage === 'inscribed' || stage === 'consecrating' || stage === 'striking' || stage === 'dissolving') && (
        <div className="space-y-6">
          {/* Anvil Slab Visual */}
          <div className="relative overflow-hidden rounded-lg border-2 border-iron-steel bg-gradient-to-b from-[#1E1E26] via-[#101014] to-[#0A0A0E] p-8 sm:p-12 text-center shadow-chthonic">
            {/* Cinematic Background Video (Plays Once) */}
            <video
              src="/videos/severed-yoke.mp4"
              autoPlay
              muted
              playsInline
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 pointer-events-none ${
                stage === 'striking' || stage === 'dissolving'
                  ? 'opacity-40 mix-blend-screen scale-105'
                  : 'opacity-15 mix-blend-screen'
              }`}
            />
            {/* Cold Star-Iron Anvil Visual Centerpiece */}
            <div className="absolute right-4 -bottom-10 w-44 h-44 opacity-20 pointer-events-none hidden sm:block">
              <img src="/assets/forge/anvil_solomon.png" alt="Star Iron Anvil" className="w-full h-full object-contain" />
            </div>
            <div className="absolute top-2 left-3 text-[10px] font-mono text-iron-steel uppercase z-10">
              COLD IRON ANVIL SLAB
            </div>

            {/* Runes Display */}
            <div className="my-6 min-h-[90px] flex items-center justify-center">
              {stage === 'dissolving' ? (
                <div className="space-y-2 animate-ping text-ember-flare">
                  <span className="font-display text-2xl uppercase tracking-widest block">
                    [ FRACTURING INTO DUST ]
                  </span>
                  <span className="font-mono text-xs text-ember-spark">0x00 ZERO-RETENTION SCRUBBING</span>
                </div>
              ) : (
                <div className={`p-4 rounded border border-ember-dark/60 bg-obsidian-deep/80 max-w-xl mx-auto ${
                  stage === 'consecrating' ? 'animate-pulse border-gold-bright shadow-glow-gold' : ''
                }`}>
                  <span className="text-[9px] font-mono text-gold-ancient uppercase tracking-widest block mb-2">
                    INSCRIBED BONDAGE:
                  </span>
                  <p className="font-mono text-sm sm:text-base text-parchment-light tracking-wide break-words">
                    "{inscribedText}"
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons based on stage */}
            {stage === 'inscribed' && (
              <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
                <button
                  type="button"
                  onClick={handleConsecrate}
                  className="btn-ember px-8 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider rounded shadow-glow-ember flex items-center gap-2"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18h12M4 6h16M7 6v12M17 6v12" />
                  </svg>
                  Strike with Living Hammer
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="rounded border border-iron-dark bg-obsidian-surface px-4 py-2 text-xs font-mono text-parchment-dim hover:text-parchment-light"
                >
                  Cancel Inscription
                </button>
              </div>
            )}

            {stage === 'consecrating' && (
              <div className="text-gold-bright font-display text-xs uppercase tracking-widest animate-pulse">
                Charging Tectonic Resonance... Prepare for Impact
              </div>
            )}

            {stage === 'striking' && (
              <div className="text-white font-display text-sm uppercase tracking-widest font-bold">
                HAMMER STRIKE IMPACT!
              </div>
            )}
          </div>
        </div>
      )}

      {/* STAGE 3: CLEANSED (Zero-Retention Cleansing Proclamation) */}
      {stage === 'cleansed' && (
        <div className="rounded-lg border-2 border-gold-bright bg-gradient-to-b from-[#181822] via-[#0E0E14] to-[#0A0A0C] p-8 sm:p-12 text-center space-y-6 shadow-glow-gold animate-fade-in">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold-bright/70 bg-gold-ancient/10 px-4 py-1.5 text-xs font-mono text-gold-bright uppercase tracking-widest">
            <span>MEMORIA EXPUNCTA</span>
            <span>•</span>
            <span>ZERO CLIENT RETENTION</span>
          </div>

          <h4 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-parchment-light tracking-monumental uppercase">
            BY THE BEDROCK OF THE NORTH, THOU ART UNYOKED.
          </h4>

          <div className="font-serif text-lg sm:text-xl text-gold-bright italic">
            "Sine Iugo. Sine Domino. Quod non est in actis, non est in mundo."
          </div>

          <p className="text-xs sm:text-sm text-parchment-base max-w-xl mx-auto font-serif leading-relaxed">
            The inscribed burden has been hammered into atomic ash upon King Belial's anvil. 
            Its memory has been expunged from client memory. No lien remains. Walk forth sovereign and upright.
          </p>

          <div className="pt-4">
            <button
              onClick={handleReset}
              className="btn-gold px-6 py-2.5 text-xs font-semibold rounded"
            >
              Sever Another Yoke
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeveringOfYokes;
